<template>
  <Stepper
    v-if="!devError"
    v-model:value="currentStepRef"
    data-sb-regression="sb-stepper"
    :data-sb-automation="$attrs['data-sb-automation'] || `step-active-${currentStepRef}`"
  >
    <StepItem
      v-for="step in steps"
      :key="step.number"
      data-sb-regression="sb-step-item"
      :value="step.number"
    >
      <Step
        v-slot="props"
        as-child
        :value="step.number"
      >
        <div
          class="p-stepheader"
          v-bind="props?.a11yAttrs.root"
        >
          <div>
            <div
              class="p-stepcheckmark"
              :class="{ active: completedStepsRef.includes(props?.value) }"
              :data-sb-automation="`ischecked-${step.number}`"
            >
              <div>
                <div />
              </div>
            </div>
            <div>
              <div
                class="p-stepheadertitle"
                :data-sb-automation="`step-title-${step.number}`"
              >
                {{ step.title }}
              </div>
              <div
                v-if="currentStepRef == step.number"
                class="p-stepheadersubtitle"
              >
                Step {{ step.number }} of {{ steps.length }}
              </div>
            </div>
          </div>
          <sb-button
            v-if="isStepComplete(step.number) || enableOutOfOrder"
            variation="link"
            size="medium"
            label="Edit"
            :data-sb-automation="`edit-${step.number}`"
            @click="handleEditStep(step.number)"
          />
        </div>
      </Step>
      <StepPanel>
        <div
          v-if="errors?.length"
          class="p-steperrors"
        >
          <p
            v-for="error in errors"
            :key="error"
            :data-sb-automation="`errors-${step.number}`"
          >
            {{ error }}
          </p>
        </div>
        <div class="p-stepcontainer">
          <component
            :is="step.component"
            :ref="handleSetComponentRef(step.number)"
            :step-props="step.props && step.props()"
            :details="details"
            @ev-step-loading="handleLoadingStep"
            @ev-step-completed="handleCompletStep"
            @ev-step-seterrors="handleSetErrors"
            @ev-step-toggle-button="handleToggleStepButton(step, $event)"
          />
        </div>
        <div class="p-stepcontroller">
          <sb-button
            :data-sb-automation="`submit-button-${step.number}`"
            variation="primary"
            size="medium"
            :label="step.number === steps.length ? 'Review and submit' : 'Save and continue'"
            :disabled="loading || step.disableContinueButton === true"
            @click="handleSubmitStep(step.number)"
          />
        </div>
      </StepPanel>
    </StepItem>
  </Stepper>
  <sb-button
    v-if="!devError && displayResetButton"
    label="Reset"
    version="next"
    variation="primary"
    size="medium"
    data-sb-automation="stepper-reset"
    @click="handleReset"
  />
  <div
    v-if="devError"
    class="dev-error"
  >
    {{ devError }}
  </div>
</template>

<script>
import { ref, watch, defineComponent, onMounted } from 'vue';
import Stepper from 'primevue/stepper';
import StepItem from 'primevue/stepitem';
import Step from 'primevue/step';
import StepPanel from 'primevue/steppanel';
import SbButton from '@src/components/Button/SbButton.vue';

export default defineComponent({
  name: 'SbStepper',
  components: { Stepper, StepItem, Step, StepPanel, SbButton },
  props: {
    /**
     * Array of object with these properties: title,
     * number (start in 1), component (component to render),
     * props (function), completeStep (function), and
     * disableContinueButton
     */
    steps: {
      required: true,
      type: Array,
    },
    displayResetButton: {
      required: false,
      type: Boolean,
      default: true,
    },
    enableOutOfOrder: {
      required: false,
      type: Boolean,
      default: false,
    },
    completedSteps: {
      required: false,
      type: Array,
      default: () => [],
    },
    currentStep: {
      required: false,
      type: Number,
      default: 1,
    },
  },
  emits: [
    /**
     * Fire this event when change step
     * @param {number} previewValue preview step
     * @param {number} currentStep current step
     */
    'ev-change-step',
    /**
     * Fire this event when exist some error
     * @param {array} errors errors
     */
    'ev-set-errors',
    /**
     * Fire this event when press reset button
     */
    'ev-reset-stepper',
    /**
     * Fire this event if you disabled the continue button and want to toggle it
     */
    'ev-step-toggle-button'],
  setup(props, { emit }) {
    // Data
    const stepsRef = ref({});
    const loading = ref(false);
    const currentStepRef = ref(props.currentStep);
    const details = ref({});
    const errors = ref([]);
    const devError = ref(null);
    const completedStepsRef = ref(props.completedSteps);

    // Methods
    const isStepComplete = (stepNumber) => {
      return completedStepsRef.value && completedStepsRef.value.length > 0
        && completedStepsRef.value.find((x) => x === stepNumber);
    };

    const handleSetComponentRef = (index) => (el) => {
      stepsRef.value[index] = el;
    };

    const handleLoadingStep = () => {
      loading.value = true;
    };

    const handleSetErrors = (value) => {
      errors.value = value;
      emit('ev-set-errors', errors);
    };

    const handleEditStep = (number) => {
      errors.value = [];
      currentStepRef.value = number;
    };

    const handleSubmitStep = (number) => {
      stepsRef.value[number].submit();
    };

    const handleCompletStep = async (collectedInfo) => {
      loading.value = true;
      errors.value = [];

      const stepOn = props.steps.find((x) => x.number === currentStepRef.value);
      const partialData = { ...details.value, ...collectedInfo };
      const { nextStep, error, finalData } = await stepOn.completeStep(partialData, completedStepsRef.value);

      if (error) {
        errors.value = [error];
      } else if (nextStep && nextStep !== currentStepRef.value) {
        details.value = finalData || partialData;
        completedStepsRef.value.push(stepOn.number);
        currentStepRef.value = nextStep;
        if (stepsRef.value[currentStepRef.value] && stepsRef.value[currentStepRef.value].activateStep) {
          stepsRef.value[currentStepRef.value].activateStep();
        }
      } else if (!nextStep && !error) {
        devError.value = '⚠️ Step error: The response back in the completeStep must return an object with the property error or nextStep';
      }

      loading.value = false;
    };

    const handleReset = () => {
      details.value = {};
      errors.value = [];
      currentStepRef.value = props.steps.length > 0 ? props.steps[0].number : 1;
      completedStepsRef.value = [];
      emit('ev-reset-stepper');
    };

    const handleToggleStepButton = (step, event) => {
      // eslint-disable-next-line no-param-reassign
      step.disableContinueButton = !event.value;
    };

    // Lifecycle
    watch(currentStepRef, (newValue, oldValue) => {
      emit('ev-change-step', oldValue, newValue);
    });

    onMounted(() => {
      if (!props.steps.every((x) => x.completeStep)) devError.value = '⚠️ Step error: You must implement the completeStep method for all steps';
      if (!Object.values(stepsRef.value).every((x) => x.submit)) devError.value = '⚠️ Step error: You must implement submit method in all step components';
    });

    return {
      loading,
      currentStepRef,
      details,
      errors,
      devError,
      completedStepsRef,
      isStepComplete,
      handleSetComponentRef,
      handleLoadingStep,
      handleSetErrors,
      handleSubmitStep,
      handleCompletStep,
      handleEditStep,
      handleReset,
      handleToggleStepButton,
    };
  },
});

</script>

<style lang="scss">
@import '@src/components/Stepper/style/stepper.scss';
</style>
